import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import SecuritySection from '../../components/common/SecuritySection';
import ProfileCompletionModal from '../../components/common/ProfileCompletionModal';

export default function EmployerDashboard() {
  const [requirements, setRequirements] = useState([]);
  const [profile,      setProfile]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [userInfo,     setUserInfo]     = useState(null);
  const [showModal,    setShowModal]    = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get('/employer/profile'),
      api.get('/requirements?status=active&limit=5'),
      api.get('/auth/me'),
    ]).then(([p, r, u]) => {
      if (p.status === 'fulfilled') setProfile(p.value.data.data);
      if (r.status === 'fulfilled') setRequirements(r.value.data.data || []);
      if (u.status === 'fulfilled') {
        const d = u.value.data.data;
        setUserInfo(d);
        if (!d.first_name || !d.last_name || !d.email || !d.has_password) {
          setShowModal(true);
        }
      }
      setLoading(false);
    });
  }, []);

  function handleModalSaved() {
    setShowModal(false);
    api.get('/auth/me').then((r) => setUserInfo(r.data.data)).catch(() => {});
  }

  if (loading) return (
    <EmployerLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full"></div>
      </div>
    </EmployerLayout>
  );

  return (
    <EmployerLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-india to-emerald-600 rounded-2xl p-5 text-white">
          <h2 className="text-xl font-bold">{profile?.company_name || 'Welcome!'}</h2>
          <p className="text-green-100 text-sm mt-1">{profile?.employer_type || 'Employer'}</p>
          {profile && (
            <Badge label={profile.verification_status} variant={profile.verification_status} className="mt-2" />
          )}
        </div>

        {/* Not Verified Warning */}
        {profile && profile.verification_status !== 'verified' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="font-medium text-yellow-800">⚠️ Profile Pending Verification</p>
            <p className="text-sm text-yellow-700 mt-1">Your employer profile is pending admin verification. You'll be able to post requirements once verified.</p>
          </div>
        )}

        {!profile && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="font-medium text-yellow-800">Complete your employer profile</p>
            <Link to="/employer/profile/create"><Button size="sm" className="mt-2">Create Profile</Button></Link>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/employer/requirements/new"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:border-brand-300 transition-colors">
            <span className="text-2xl">📋</span>
            <p className="text-sm font-medium text-gray-900 mt-2">Post Requirement</p>
          </Link>
          <Link to="/employer/workers/search"
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:border-brand-300 transition-colors">
            <span className="text-2xl">🔍</span>
            <p className="text-sm font-medium text-gray-900 mt-2">Search Workers</p>
          </Link>
        </div>

        {/* My Requirements */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">My Active Requirements</h3>
            <Link to="/employer/requirements" className="text-sm text-brand-600 hover:underline">View all</Link>
          </div>
          {requirements.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <span className="text-3xl">📋</span>
              <p className="text-gray-500 mt-2">No active requirements</p>
              <Link to="/employer/requirements/new"><Button size="sm" className="mt-3">Post First Requirement</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {requirements.map((r) => (
                <Link key={r.id} to={`/employer/requirements/${r.id}`}
                  className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-300 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{r.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{r.district} · {r.applications_count} applications</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge label={r.status} variant={r.status} />
                      <Badge label={r.urgency} variant={r.urgency} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ── Login & Security ── */}
        <SecuritySection readOnlyEmail />
      </div>

      {showModal && userInfo && (
        <ProfileCompletionModal
          userData={userInfo}
          onClose={() => setShowModal(false)}
          onSaved={handleModalSaved}
        />
      )}
    </EmployerLayout>
  );
}
